import {
  Banner,
  Button,
  Checkbox,
  Field,
  IconButton,
  Kbd,
  Input,
  Modal,
  TD,
  TH,
  Table,
  cx,
  Textarea,
  useToast,
} from '@ims/ui'
import { AlertTriangle, Check, Package, Sparkles, X } from '@ims/ui/icons'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import {
  billDiscountPaisa,
  lineExtraPaisa,
  parseSerials,
  defaultOtherPrice,
  receiptLineAmount,
  receiptTrueCosts,
  toStockQty,
  toStockRate,
  unitName,
  type TwoUnits,
  type UnitChoice,
  setPurchaseOrderStatus,
  shelfLifeLabel,
  type PercentOrAmount,
} from '@ims/core'
import type { ProductCodeRow, ProductRow, PurchaseOrderRow, RowOf } from '@ims/data'
import { LineDiscountInput } from '../discount-input.tsx'
import {
  DateInput,
  LocationSelect,
  MoneyInput,
  ProductPicker,
  QtyInput,
  useLookups,
  UnitChoiceSwitch,
} from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { clearDraft, draftKey, readDraft, saveDraft } from '../lib/drafts.ts'
import { dateLabel, money, qty, todayIso } from '../lib/format.ts'
import { percentText } from '../lib/discount-mode.ts'
import { calendarOf } from '../lib/reports.ts'
import { expiryText } from '@ims/core'
import { followExpiry, parseExpiry, shelfOf } from '../lib/batches.ts'
import { purchaseOrderRow } from '../lib/purchases.ts'
import { rateAgainstSaved, rateLooksOff, savedCostChanges } from '../lib/rate-check.ts'
import {
  draftHasWork,
  draftTotal,
  lineAmount,
  parseReceiveDraft,
  restoreLines,
  type ReceiveDraft,
} from '../lib/receive-draft.ts'
import { receiveGoods, updateOp, type SerialRow } from '../lib/rows.ts'
import { loadSerials, receiveProblem } from '../lib/serials.ts'
import { SupplierPicker } from '../party-picker.tsx'
import { businessKinds } from '../lib/catalog.ts'
import { ReceiveGrid } from './sizes-colours.tsx'
import { useRepo, useWorkspace } from '../workspace.tsx'
import { finePointer } from '../lib/pointer.ts'
import { lineKeyDown } from '../lib/line-keys.ts'
import { ScanButton, ScanSerialsButton } from '../scanner.tsx'
import { DraftBanner, DroppedNote } from './draft-banner.tsx'
import {
  BillFields,
  billDraftOps,
  billDraftAdvance,
  billDraftProblem,
  emptyBillDraft,
  type BillDraft,
} from './supplier-bills.tsx'

/** Bills print 04/2027 or 2027/12: the month is enough, and the box shows the date it means. */
function ExpiryInput({
  name,
  iso,
  className,
  hint,
  onChange,
}: {
  name: string
  iso: string
  className?: string
  hint?: string
  onChange: (iso: string) => void
}) {
  const [text, setText] = useState(expiryText(iso))
  const [last, setLast] = useState(iso)
  if (iso !== last) {
    setLast(iso)
    if ((parseExpiry(text) ?? '') !== iso) setText(expiryText(iso))
  }
  return (
    <Input
      aria-label={`Expiry date of ${name}`}
      title="Expiry: 2027/04 or 2027-04-30"
      placeholder={hint}
      inputMode="numeric"
      className={className}
      value={text}
      aria-invalid={text.trim() !== '' && !parseExpiry(text)}
      onChange={(e) => {
        setText(e.target.value)
        onChange(parseExpiry(e.target.value) ?? '')
      }}
      onBlur={() => {
        const saved = parseExpiry(text)
        setText(saved ? expiryText(saved) : text)
      }}
    />
  )
}

const unitsOf = (row: RowOf<'product_units'>): TwoUnits => ({
  larger_name: row.larger_name,
  smaller_name: row.smaller_name,
  count: row.count,
  stock_in: row.stock_in as UnitChoice,
})

const OPEN_BOX: PercentOrAmount = { mode: 'percent', bp: null, paisa: 0 }

/** The two ways a line's box takes its number, in the shape the discount box wants. */
function PercentBox({
  name,
  placeholder,
  box,
  onChange,
}: {
  name: string
  placeholder: string
  box: PercentOrAmount
  onChange: (box: PercentOrAmount) => void
}) {
  return (
    <LineDiscountInput
      className="w-36"
      description={name}
      name={name}
      placeholder={placeholder}
      mode={box.mode}
      discountPaisa={box.paisa}
      percentBp={box.bp}
      onAmount={(paisa) => onChange({ mode: 'amount', bp: null, paisa })}
      onPercent={(bp) => onChange({ mode: 'percent', bp, paisa: 0 })}
      onMode={(mode) => onChange({ mode, bp: null, paisa: 0 })}
    />
  )
}

const LINK = 'text-sm font-semibold text-primary hover:underline'

function ExtraBox({
  label,
  name,
  box,
  result,
  onChange,
}: {
  label: string
  name: string
  box: PercentOrAmount
  result: string
  onChange: (box: PercentOrAmount | null) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
      <span className="font-medium">{label}</span>
      <PercentBox name={name} placeholder="0" box={box} onChange={onChange} />
      <span className="tabular font-semibold">{result}</span>
      <IconButton label={`Remove ${label.toLowerCase()}`} onClick={() => onChange(null)}>
        <X className="size-4" />
      </IconButton>
    </div>
  )
}

/** A rupee or less is the supplier's round-off; more is worth a look, and never blocks. */
const ROUND_OFF_LIMIT = 100

function BillCheck({
  billPaisa,
  linesPaisa,
  shown,
}: {
  billPaisa: number
  linesPaisa: number
  shown: (paisa: number) => string
}) {
  const gap = billPaisa - linesPaisa
  if (Math.abs(gap) <= ROUND_OFF_LIMIT)
    return (
      <Banner tone="success">
        Matches the paper bill.
        {gap !== 0 && ` ${shown(Math.abs(gap))} is the supplier’s round-off.`}
      </Banner>
    )
  return (
    <Banner tone="warning">
      {shown(Math.abs(gap))} {gap > 0 ? 'more' : 'less'} than the lines. Check a quantity or rate,
      or save with the paper’s total.
    </Banner>
  )
}

/** How the bill is paid: most are on credit, so this stays behind one link in the footer. */
function BillPaymentDialog({
  draft,
  onChange,
  onClose,
}: {
  draft: BillDraft
  onChange: (patch: Partial<BillDraft>) => void
  onClose: () => void
}) {
  const total = draft.totalPaisa ?? 0
  const paid = draft.paidPaisa ?? 0
  const [part, setPart] = useState(paid > 0 && paid !== total && !draft.paidFull)
  const kind = part ? 'part' : paid > 0 ? 'full' : 'credit'
  const choices = [
    {
      value: 'credit',
      label: 'On credit',
      pick: () => onChange({ paidPaisa: null, paidFull: false }),
    },
    {
      value: 'full',
      label: 'Paid in full',
      pick: () => onChange({ paidPaisa: total, paidFull: true }),
    },
    {
      value: 'part',
      label: 'Paid another amount',
      pick: () => onChange({ paidPaisa: null, paidFull: false }),
    },
  ]
  return (
    <Modal
      open
      onClose={onClose}
      title="How this bill is paid"
      size="sm"
      unsaved={false}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="space-y-4">
        <div role="radiogroup" aria-label="How this bill is paid" className="grid gap-0.5">
          {choices.map((c) => (
            <button
              key={c.value}
              type="button"
              role="radio"
              aria-checked={kind === c.value}
              onClick={() => {
                setPart(c.value === 'part')
                c.pick()
              }}
              className={cx(
                'flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm',
                kind === c.value
                  ? 'bg-primary-light font-semibold text-primary'
                  : 'text-ink hover:bg-soft',
              )}
            >
              {c.label}
              {kind === c.value && <Check className="size-4" />}
            </button>
          ))}
        </div>
        {kind !== 'full' && kind !== 'credit' && (
          <BillFields draft={draft} onChange={onChange} paymentOnly />
        )}
        {kind === 'credit' && <BillFields draft={draft} onChange={onChange} paymentOnly dueOnly />}
      </div>
    </Modal>
  )
}

// Taking a delivery into stock: from the Stock screen (anything, from anyone)
// or against a purchase order (what is still due on it, from its supplier).

interface ReceiveLine {
  key: string
  product: ProductRow
  qtyMilli: number | null
  costPaisa: number | null
  batchNo: string
  mfgDate: string
  expiryDate: string
  /** The expiry was worked out from the mfg date and shelf life (it follows the mfg date). */
  expiryAuto?: boolean
  mrpPaisa: number | null
  /** Serial-tracked products: one serial per line; the quantity is their count. */
  serialsText: string
  /** The purchase order line this fills. */
  orderLineId?: string
  /** Came free, beside what was paid for (docs/plans/free-goods.md). */
  freeMilli: number | null
  charge: PercentOrAmount | null
  discount: PercentOrAmount | null
  /** A product in two units (docs/plans/two-units.md): qty, free and rate are typed in this one. */
  twoUnits?: RowOf<'product_units'> | null
  enteredChoice: UnitChoice | null
}

/** What is still due on a purchase order line, to start a receipt from. */
// Phones hide the table's header, so quantity and cost carry their names.
const PHONE_LABEL =
  'max-sm:before:mb-1 max-sm:before:block max-sm:before:text-xs max-sm:before:font-medium max-sm:before:text-muted'
// A box on a line that is not being typed: it reads as plain text until the line is chosen.
const REST = 'border-transparent! bg-transparent! shadow-none!'

/** A rate that looks wrong, in the words the line and the check share. */
interface RateOff {
  index: number
  name: string
  typed: string
  /** "Rs. 1.67 per Piece" on a line typed in the other unit, else how far off it is. */
  makes: string | null
  against: string
  saved: string
  atSaved: string | null
}

/** Asked once, at Receive, only about lines that look wrong. Enter changes the rate. */
function RateCheckDialog({
  off,
  onChange,
  onReceive,
  onClose,
}: {
  off: RateOff[]
  onChange: () => void
  onReceive: (alsoSaved: boolean) => void
  onClose: () => void
}) {
  const [alsoSaved, setAlsoSaved] = useState(false)
  const change = useRef<HTMLDivElement>(null)
  // Modal focuses its first button, which here would save on Enter. This runs after its effect.
  useEffect(() => {
    change.current?.querySelector('button')?.focus()
    // Escape leaves this check, not Receive stock under it.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      onClose()
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [onClose])
  const one = off.length === 1 ? off[0] : null
  const fact = 'flex justify-between gap-4 border-t border-border py-1.5'
  return (
    <Modal
      open
      onClose={onClose}
      unsaved={false}
      size="sm"
      title={one ? 'Check this rate' : `Check ${off.length} rates`}
      footer={
        <>
          <Button variant="secondary" onClick={() => onReceive(alsoSaved)}>
            Receive anyway
          </Button>
          <div ref={change}>
            <Button onClick={onChange}>{one ? 'Change rate' : 'Change rates'}</Button>
          </div>
        </>
      }
    >
      {one ? (
        <div className="text-sm">
          <p className="mb-2 font-semibold">{one.name}</p>
          <div className={fact}>
            <span className="text-muted">You typed</span>
            <span className="tabular font-medium">{one.typed}</span>
          </div>
          <div className={fact}>
            <span className="text-muted">{one.makes ? 'That makes' : 'That is'}</span>
            <span className="tabular text-right font-semibold text-warning">
              {one.makes ?? one.against}
            </span>
          </div>
          <div className={fact}>
            <span className="text-muted">Your saved cost</span>
            <span className="tabular font-medium">{one.saved}</span>
          </div>
          {one.atSaved && <p className="mt-2 text-muted">{one.atSaved}</p>}
        </div>
      ) : (
        <div className="text-sm">
          <p className="mb-2 text-muted">These are far from your saved cost.</p>
          {off.map((o) => (
            <div key={o.index} className="border-t border-border py-2">
              <p className="font-semibold">{o.name}</p>
              <p className="tabular text-muted">
                {o.typed}. Your saved cost is {o.saved}.
              </p>
              <p className="tabular font-semibold text-warning">
                {o.makes ? `That makes ${o.makes}.` : `${o.against}.`}
              </p>
            </div>
          ))}
        </div>
      )}
      <Checkbox
        className="mt-3"
        label={one ? 'Also make this my saved cost' : 'Also make these my saved costs'}
        checked={alsoSaved}
        onChange={(e) => setAlsoSaved(e.target.checked)}
      />
    </Modal>
  )
}

export interface DueLine {
  lineId: string
  product: ProductRow
  qtyMilli: number
  costPaisa: number
}

export function ReceiveStockDialog({
  onClose,
  order,
  products,
}: {
  onClose: () => void
  /** Receive against this purchase order: its supplier, and its lines still due. */
  order?: { order: PurchaseOrderRow; due: DueLine[] }
  /**
   * Start with these on the lines, quantities to fill in (Receive on a list's low or out
   * row); the first one's usual supplier is chosen.
   */
  products?: readonly ProductRow[]
}) {
  const repo = useRepo()
  const settings = useSettings()
  // A clothing shop picks a product once and types its sizes and colours in a grid.
  const clothing = businessKinds(settings).includes('apparel')
  const [gridOf, setGridOf] = useState<ProductRow | null>(null)
  const lookups = useLookups()
  const { current } = useWorkspace()
  const toast = useToast()
  const [supplierId, setSupplierId] = useState(
    order?.order.supplier_id ?? products?.[0]?.supplier_id ?? '',
  )
  const [locationId, setLocationId] = useState<string | null>(order?.order.location_id ?? null)
  const [reference, setReference] = useState('')
  const [lines, setLines] = useState<ReceiveLine[]>(() => {
    const blank = {
      batchNo: '',
      mfgDate: '',
      expiryDate: '',
      mrpPaisa: null,
      serialsText: '',
      freeMilli: null,
      charge: null,
      discount: null,
      enteredChoice: null,
    }
    if (order)
      return order.due.map((d) => ({
        ...blank,
        key: crypto.randomUUID(),
        product: d.product,
        qtyMilli: d.product.track_serials ? null : d.qtyMilli,
        costPaisa: d.costPaisa,
        orderLineId: d.lineId,
      }))
    // Started from a list's row: those products, with their last cost, quantity to fill in.
    return (products ?? []).map((p) => ({
      ...blank,
      key: crypto.randomUUID(),
      product: p,
      qtyMilli: null,
      costPaisa: p.cost_price_paisa || null,
    }))
  })
  const [busy, setBusy] = useState(false)
  // The supplier's bill: its total follows the lines until it is typed.
  const [bill, setBill] = useState<BillDraft>(emptyBillDraft)
  const [billTyped, setBillTyped] = useState(false)
  const [billDiscount, setBillDiscount] = useState<PercentOrAmount | null>(null)
  const [paying, setPaying] = useState(false)
  // The line being typed: only it shows its boxes (docs/plans/free-goods.md).
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const pickerRef = useRef<HTMLInputElement>(null)
  const linesRef = useRef<HTMLTableSectionElement>(null)
  const [checking, setChecking] = useState(false)

  // What was typed is kept on this device as it is typed (docs/plans/dialog-safety.md): one
  // draft for receiving without an order, one per purchase order.
  const keyOfDraft = current
    ? draftKey(current.tenant_id, 'receive', order?.order.id ?? 'free')
    : null
  const packed: ReceiveDraft = {
    supplierId,
    locationId,
    reference,
    bill,
    billTyped,
    billDiscount,
    lines: lines.map((l) => ({
      productId: l.product.id,
      qtyMilli: l.qtyMilli,
      costPaisa: l.costPaisa,
      batchNo: l.batchNo,
      mfgDate: l.mfgDate,
      expiryDate: l.expiryDate,
      expiryAuto: l.expiryAuto ?? false,
      mrpPaisa: l.mrpPaisa,
      serialsText: l.serialsText,
      orderLineId: l.orderLineId ?? null,
      freeMilli: l.freeMilli,
      charge: l.charge,
      discount: l.discount,
      enteredChoice: l.enteredChoice,
    })),
  }
  const packedText = JSON.stringify(packed)
  // Lines a list or an order filled in are not work yet: only a change from the start is.
  const [startText] = useState(packedText)
  const touched = packedText !== startText && draftHasWork(packed)
  // A draft from before, offered until it is continued or thrown away; meanwhile nothing
  // is saved over it.
  const [offer, setOffer] = useState(() =>
    keyOfDraft ? readDraft(keyOfDraft, parseReceiveDraft) : null,
  )
  const [dropped, setDropped] = useState(0)
  useEffect(() => {
    if (!keyOfDraft || offer) return
    const timer = window.setTimeout(() => {
      if (touched) saveDraft(keyOfDraft, JSON.parse(packedText) as ReceiveDraft)
      else clearDraft(keyOfDraft)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [keyOfDraft, offer, touched, packedText])
  const linesWord = (n: number) => `${n} line${n === 1 ? '' : 's'}`
  // X, Escape and Cancel close at once; the work stays as the draft.
  const leave = () => {
    if (keyOfDraft && !offer && touched) {
      saveDraft(keyOfDraft, packed)
      toast.info('Draft kept', {
        detail: `${linesWord(lines.length)} · open Receive stock again to go on`,
      })
    }
    onClose()
  }
  const continueDraft = async () => {
    if (!offer) return
    const found = new Map<string, ProductRow>()
    for (const id of new Set(offer.value.lines.map((l) => l.productId))) {
      const p = await repo.get('products', id)
      if (p && p.is_active && !p.deleted_at) found.set(id, p)
    }
    const due = order ? new Set(order.due.map((d) => d.lineId)) : null
    const restored = restoreLines(offer.value, found, due)
    if (!order) setSupplierId(offer.value.supplierId)
    setLocationId(offer.value.locationId)
    setReference(offer.value.reference)
    setBill({ ...offer.value.bill, method: offer.value.bill.method as BillDraft['method'] })
    setBillTyped(offer.value.billTyped)
    setBillDiscount(offer.value.billDiscount)
    setLines(
      restored.lines.map(({ productId, orderLineId, ...l }) => {
        const key = crypto.randomUUID()
        fillUnits(key, productId, l.enteredChoice)
        return { ...l, key, orderLineId: orderLineId ?? undefined }
      }),
    )
    setDropped(restored.dropped)
    setOffer(null)
  }
  const startNew = () => {
    if (keyOfDraft) clearDraft(keyOfDraft)
    setOffer(null)
  }
  const location = locationId ?? lookups.activeLocations[0]?.id ?? null
  const paidOf = (l: ReceiveLine) =>
    (l.product.track_serials ? parseSerials(l.serialsText).serials.length * 1000 : l.qtyMilli) ?? 0
  const linesTotal = lines.reduce((a, l) => a + lineAmount({ ...l, qty: paidOf(l) }), 0)
  const billOff = billDiscountPaisa(linesTotal, billDiscount)
  const total = linesTotal - billOff
  const moneyOf = (l: ReceiveLine) => {
    const qty = paidOf(l)
    const ratePaisa = l.costPaisa ?? 0
    return {
      qtyMilli: qty,
      freeMilli: l.freeMilli ?? 0,
      ratePaisa,
      discountPaisa: lineExtraPaisa(qty, ratePaisa, l.discount),
      freeChargePaisa: lineExtraPaisa(l.freeMilli ?? 0, ratePaisa, l.charge),
    }
  }
  const trueCosts = receiptTrueCosts(lines.map(moneyOf), billOff)
  // What is stored: always in the unit stock is counted in, with what was typed beside it.
  const typedAs = (l: ReceiveLine) => {
    const m = moneyOf(l)
    const u = l.twoUnits && l.enteredChoice ? unitsOf(l.twoUnits) : null
    if (!u || !l.enteredChoice)
      return {
        qtyMilli: qtyOf(l) ?? 0,
        unitCostPaisa: m.ratePaisa,
        freeMilli: m.freeMilli,
      }
    const converted = l.enteredChoice !== u.stock_in
    return {
      qtyMilli: toStockQty(m.qtyMilli, l.enteredChoice, u),
      unitCostPaisa: toStockRate(m.ratePaisa, l.enteredChoice, u),
      freeMilli: toStockQty(m.freeMilli, l.enteredChoice, u),
      amountPaisa: converted ? receiptLineAmount(m) : null,
      enteredUnit: unitName(u, l.enteredChoice),
      enteredQtyMilli: m.qtyMilli,
    }
  }
  // Saved cost and the rate compared in the unit stock is counted in.
  const rateOf = (l: ReceiveLine) => {
    const u = l.twoUnits && l.enteredChoice ? unitsOf(l.twoUnits) : null
    const rate = l.costPaisa ?? 0
    const stockRate = u && l.enteredChoice ? toStockRate(rate, l.enteredChoice, u) : rate
    const other = u && l.enteredChoice !== u.stock_in ? u : null
    const per = (name: string | undefined) => (name ? ` per ${name}` : '')
    return {
      rate,
      stockRate,
      saved: l.product.cost_price_paisa,
      perPiece: other ? `${money(stockRate, settings)}${per(unitName(other, other.stock_in))}` : '',
      stockUnit: per(u ? unitName(u, u.stock_in) : undefined),
      typedUnit: per(u && l.enteredChoice ? unitName(u, l.enteredChoice) : undefined),
      other,
      off: paidOf(l) > 0 && rateLooksOff(stockRate, l.product.cost_price_paisa),
    }
  }
  const offLines: RateOff[] = lines.flatMap((l, index) => {
    const r = rateOf(l)
    if (!r.off || !r.saved) return []
    return [
      {
        index,
        name: l.product.name,
        typed: `${money(r.rate, settings)}${r.typedUnit}`,
        makes: r.other ? r.perPiece : null,
        against: rateAgainstSaved(r.stockRate, r.saved),
        saved: `${money(r.saved, settings)}${r.stockUnit}`,
        atSaved:
          r.other && l.enteredChoice
            ? `At your saved cost, 1 ${unitName(r.other, l.enteredChoice)} would be ${money(defaultOtherPrice(r.saved, r.other), settings)}.`
            : null,
      },
    ]
  })
  const closeCheck = useCallback(() => setChecking(false), [])
  const changeRate = () => {
    const first = offLines[0]
    setChecking(false)
    if (!first) return
    setActiveKey(lines[first.index]?.key ?? null)
    // After the check has closed and given focus back.
    requestAnimationFrame(() => {
      const box = linesRef.current?.querySelector<HTMLInputElement>(
        `tr[data-main][data-line="${first.index}"] input[aria-label^="Cost of"]`,
      )
      box?.focus()
      box?.select()
    })
  }
  const missingBatch = lines.some((l) => l.product.track_batches && !l.batchNo.trim())
  const missingExpiry = lines.some(
    (l) => l.product.track_batches && l.product.track_expiry && !l.expiryDate,
  )
  const qtyOf = (l: ReceiveLine) =>
    l.product.track_serials ? parseSerials(l.serialsText).serials.length * 1000 : l.qtyMilli
  const needsQty = lines.some((l) => !qtyOf(l) && !l.freeMilli)
  const billed: BillDraft = billTyped ? bill : { ...bill, totalPaisa: total || null }
  const billDraft: BillDraft = billed.paidFull
    ? { ...billed, paidPaisa: billed.totalPaisa }
    : billed
  const billProblem = supplierId ? billDraftProblem(billDraft) : null
  const invalid =
    lines.length === 0 ||
    lines.some((l) => !qtyOf(l) && !l.freeMilli) ||
    !location ||
    missingBatch ||
    missingExpiry ||
    !!billProblem
  const batchesOf =
    useLive(
      async (r) => {
        const ids = [
          ...new Set(lines.filter((l) => l.product.track_batches).map((l) => l.product.id)),
        ]
        const out: Record<string, RowOf<'batches'>[]> = {}
        for (const id of ids) {
          out[id] = (await r.list('batches', { where: { product_id: id }, limit: 500 })).rows
        }
        return out
      },
      [lines.map((l) => l.product.id).join(',')],
      ['batches'],
    ).data ?? {}

  // Batch products can be received as several batches: each scan adds a line.
  // The rate of the last bill for this product, unless one has been typed meanwhile.
  const fillLastRate = (product: ProductRow) => {
    void (async () => {
      const last =
        (await repo.purchases.lastReceiptLine?.(product.id, supplierId || null)) ??
        (await repo.purchases.lastReceiptLine?.(product.id, null))
      if (!last?.unit_cost_paisa) return
      setLines((ls) =>
        ls.map((l) =>
          l.product.id === product.id && l.costPaisa === product.cost_price_paisa && !l.orderLineId
            ? { ...l, costPaisa: last.unit_cost_paisa }
            : l,
        ),
      )
    })()
  }
  // The charge this supplier last put on this product's free goods: a percent as it was, exact
  // rupees only for the same free quantity.
  const suggestCharge = (key: string, product: ProductRow, freeMilli: number | null) => {
    if (!freeMilli || !supplierId) return
    void (async () => {
      const last = await repo.purchases.lastReceiptLine?.(product.id, supplierId)
      if (!last?.free_charge_paisa) return
      const box: PercentOrAmount | null =
        last.free_charge_bp !== null
          ? { mode: 'percent', bp: last.free_charge_bp, paisa: 0 }
          : last.free_qty_milli === freeMilli
            ? { mode: 'amount', bp: null, paisa: last.free_charge_paisa }
            : null
      if (box)
        setLines((ls) => ls.map((l) => (l.key === key && !l.charge ? { ...l, charge: box } : l)))
    })()
  }
  // A product in two units gets its choice, in the unit stock is counted in to start with.
  const fillUnits = (key: string, productId: string, choice?: UnitChoice | null) => {
    void repo.list('product_units', { where: { product_id: productId }, limit: 1 }).then((r) => {
      const row = r.rows[0]
      if (!row) return
      setLines((ls) =>
        ls.map((l) =>
          l.key === key
            ? { ...l, twoUnits: row, enteredChoice: choice ?? (row.stock_in as UnitChoice) }
            : l,
        ),
      )
    })
  }
  const switchUnit = (i: number, l: ReceiveLine, choice: UnitChoice) => {
    if (!l.twoUnits || !l.enteredChoice || choice === l.enteredChoice) return
    const stockRate = toStockRate(l.costPaisa ?? 0, l.enteredChoice, unitsOf(l.twoUnits))
    update(i, {
      enteredChoice: choice,
      costPaisa:
        choice === l.twoUnits.stock_in
          ? stockRate
          : defaultOtherPrice(stockRate, unitsOf(l.twoUnits)),
    })
  }
  useEffect(() => {
    for (const p of products ?? []) fillLastRate(p)
    for (const l of lines) fillUnits(l.key, l.product.id)
    // Once, for the lines the dialog opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // A carton's barcode brings its quantity (docs/plans/free-goods.md, part 3).
  // `qtyMilli`: how many came, from Receive's grid of sizes and colours.
  const add = (product: ProductRow, pack?: ProductCodeRow | null, qtyMilli?: number) => {
    fillLastRate(product)
    const newKey = crypto.randomUUID()
    setActiveKey(newKey)
    if (!pack) fillUnits(newKey, product.id)
    const more = pack?.qty_milli ?? qtyMilli
    setLines((ls) =>
      !product.track_batches && ls.some((l) => l.product.id === product.id)
        ? more
          ? ls.map((l) =>
              l.product.id === product.id ? { ...l, qtyMilli: (l.qtyMilli ?? 0) + more } : l,
            )
          : ls
        : [
            ...ls,
            {
              key: newKey,
              product,
              qtyMilli: product.track_serials ? null : (more ?? null),
              costPaisa: product.cost_price_paisa || null,
              batchNo: '',
              mfgDate: '',
              expiryDate: '',
              mrpPaisa: null,
              serialsText: '',
              freeMilli: null,
              charge: null,
              discount: null,
              enteredChoice: null,
            },
          ],
    )
  }
  const update = (i: number, patch: Partial<ReceiveLine>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)))
  // A known batch number fills in its dates; with a shelf life, expiry follows mfg.
  const onBatchNo = (i: number, value: string) => {
    const l = lines[i]
    if (!l) return
    const known = (batchesOf[l.product.id] ?? []).find(
      (b) => b.batch_no.trim().toLowerCase() === value.trim().toLowerCase(),
    )
    update(i, {
      batchNo: value,
      ...(known
        ? {
            mfgDate: known.mfg_date ?? '',
            expiryDate: known.expiry_date ?? '',
            expiryAuto: false,
            mrpPaisa: known.mrp_paisa,
          }
        : {}),
    })
  }
  const onMfg = (i: number, value: string) => {
    const l = lines[i]
    if (!l) return
    const next = followExpiry(
      value,
      { expiry: l.expiryDate, auto: l.expiryAuto ?? false },
      shelfOf(l.product),
    )
    update(i, { mfgDate: value, expiryDate: next.expiry, expiryAuto: next.auto })
  }

  const save = async (alsoOffRates = false) => {
    if (!current || !location) return
    // Serials: each one once, and not a unit that is in stock already.
    const serialLines = new Map<string, { serial: string; existing: SerialRow | null }[]>()
    const seen = new Set<string>()
    for (const l of lines) {
      if (!l.product.track_serials) continue
      const { serials, duplicates } = parseSerials(l.serialsText)
      const repeated = duplicates[0] ?? serials.find((x) => seen.has(`${l.product.id}:${x}`))
      if (repeated) return toast.error(`Serial ${repeated} is entered twice`)
      serials.forEach((x) => seen.add(`${l.product.id}:${x}`))
      const found = await loadSerials(repo, current.tenant_id, l.product.id, serials)
      for (const [serial, existing] of found) {
        const problem = receiveProblem(serial, existing)
        if (problem) return toast.error(`${l.product.name}: ${problem}`)
      }
      serialLines.set(
        l.key,
        [...found].map(([serial, existing]) => ({ serial, existing })),
      )
    }
    setBusy(true)
    try {
      const receiptId = crypto.randomUUID()
      const now = new Date().toISOString()
      // Goods that come before a generated order is sent mark it sent (docs/plans/po-ready.md).
      const stored = order ? await repo.get('purchase_orders', order.order.id) : null
      const markSent =
        stored?.status === 'issued'
          ? setPurchaseOrderStatus(
              { tenantId: current.tenant_id, now },
              purchaseOrderRow(stored),
              'sent',
            ).ops
          : []
      // A rate that passed the check becomes the saved cost, in the same commit. Read now: the
      // whole row is pushed, so it must not carry an older price from when this dialog opened.
      const savedCosts = savedCostChanges(
        lines.map((l) => {
          const r = rateOf(l)
          return {
            productId: l.product.id,
            paid: paidOf(l) > 0,
            ratePaisa: r.stockRate,
            off: r.off,
          }
        }),
        alsoOffRates,
      )
      const costOps = (
        await Promise.all(
          [...savedCosts].map(async ([id, cost]) => {
            const row = await repo.get('products', id)
            return row && row.cost_price_paisa !== cost
              ? [updateOp('products', row, { cost_price_paisa: cost })]
              : []
          }),
        )
      ).flat()
      await repo.commit([
        ...markSent,
        ...costOps,
        ...receiveGoods(
          { tenantId: current.tenant_id },
          {
            receiptId,
            locationId: location,
            purchaseOrderId: order?.order.id ?? null,
            supplierId: supplierId || null,
            reference: reference.trim() || null,
            receiptDate: todayIso(),
            discountPaisa: billOff,
            discountBp: billDiscount?.mode === 'percent' ? billDiscount.bp : null,
            lines: lines.map((l) => ({
              productId: l.product.id,
              purchaseOrderLineId: l.orderLineId ?? null,
              ...typedAs(l),
              serials: serialLines.get(l.key),
              freeChargePaisa: moneyOf(l).freeChargePaisa,
              freeChargeBp: l.freeMilli && l.charge?.mode === 'percent' ? l.charge.bp : null,
              discountPaisa: moneyOf(l).discountPaisa,
              discountBp: l.discount?.mode === 'percent' ? l.discount.bp : null,
              batch:
                l.product.track_batches && l.batchNo.trim()
                  ? {
                      batchNo: l.batchNo,
                      mfgDate: l.mfgDate || null,
                      expiryDate: l.expiryDate || null,
                      mrpPaisa: l.mrpPaisa,
                      existing:
                        (batchesOf[l.product.id] ?? []).find(
                          (b) => b.batch_no.trim().toLowerCase() === l.batchNo.trim().toLowerCase(),
                        ) ?? null,
                    }
                  : undefined,
            })),
          },
        ),
        // The supplier's bill and what was paid at the door, in the same commit.
        ...(supplierId
          ? billDraftOps({ tenantId: current.tenant_id, now }, billDraft, {
              supplierId,
              goodsReceiptId: receiptId,
              billNo: reference,
            })
          : []),
      ])
      if (keyOfDraft) clearDraft(keyOfDraft)
      toast.success(`Received ${lines.length} item${lines.length === 1 ? '' : 's'}`)
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  const showBatch = lines.some((l) => l.product.track_batches)
  const showExpiry = lines.some((l) => l.product.track_batches && l.product.track_expiry)
  const columns = 5 + (showBatch ? 2 : 0) + (showExpiry ? 1 : 0)
  const units = lines.reduce((a, l) => a + paidOf(l) + (l.freeMilli ?? 0), 0)
  const freeUnits = lines.reduce((a, l) => a + (l.freeMilli ?? 0), 0)
  // Cartons and pieces don't add up to one number, as on the quick bill footer.
  const mixedUnits = lines.some((l) => l.twoUnits)
  const patchBill = (patch: Partial<BillDraft>) => {
    if ('totalPaisa' in patch) setBillTyped(true)
    setBill((b) => ({ ...(billTyped ? b : billDraft), ...patch }))
  }
  const paidNow = billDraft.paidPaisa ?? 0
  const paymentWords = !paidNow
    ? billDraft.dueDate
      ? `On credit, due ${dateLabel(billDraft.dueDate, settings)}`
      : 'On credit'
    : paidNow >= (billDraft.totalPaisa ?? 0)
      ? billDraftAdvance(billDraft) > 0
        ? `Paid in full, ${money(billDraftAdvance(billDraft), settings)} kept as an advance`
        : 'Paid in full'
      : `${money(paidNow, settings)} now`
  return (
    <Modal
      open
      onClose={leave}
      title={order ? `Receive ${order.order.number ?? 'order'}` : 'Receive stock'}
      subtitle="Copy the supplier’s bill, top to bottom"
      size="wide"
      area="buy"
      icon={<Package className="size-5" />}
      unsaved={
        touched &&
        (lines.length > 0
          ? `${linesWord(lines.length)} not received yet`
          : 'The bill number is not saved yet')
      }
      footer={
        <>
          <div className="mr-auto min-w-0 self-end text-sm text-muted max-sm:basis-full">
            {/* One next step at a time. */}
            {lines.length === 0 ? (
              <p>Nothing added yet</p>
            ) : needsQty ? (
              <p className="font-medium text-ink">{linesWord(lines.length)}. Enter the quantity.</p>
            ) : (
              <p>
                <b className="font-semibold text-ink">
                  {mixedUnits ? linesWord(lines.length) : qty(units)} into stock
                </b>
                {freeUnits > 0 &&
                  (mixedUnits ? ', with free goods' : `, ${qty(freeUnits)} of them free`)}
              </p>
            )}
            {!needsQty && missingBatch && (
              <p className="text-warning">
                Enter the batch no. for{' '}
                {lines.find((l) => l.product.track_batches && !l.batchNo.trim())?.product.name}
              </p>
            )}
            {!needsQty && !missingBatch && missingExpiry && (
              <p className="text-warning">
                Enter the expiry date for{' '}
                {
                  lines.find(
                    (l) => l.product.track_batches && l.product.track_expiry && !l.expiryDate,
                  )?.product.name
                }
              </p>
            )}
            {!needsQty && billProblem && <p className="text-warning">{billProblem}</p>}
            {lines.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-x-4">
                {!billDiscount && (
                  <button type="button" className={LINK} onClick={() => setBillDiscount(OPEN_BOX)}>
                    Add bill discount
                  </button>
                )}
                {supplierId && !!billDraft.totalPaisa && (
                  <button type="button" className={LINK} onClick={() => setPaying(true)}>
                    Paid: {paymentWords}
                  </button>
                )}
              </div>
            )}
          </div>
          {lines.length > 0 && (
            <div className="self-end text-right max-sm:hidden">
              <p className="mb-1 text-sm font-medium text-muted">Lines add up to</p>
              <p className="tabular flex h-10 items-center justify-end font-medium">
                {money(total, settings)}
              </p>
            </div>
          )}
          {supplierId && lines.length > 0 && (
            <div className="w-56 self-end max-sm:w-full">
              <Field label="Total on the paper bill">
                {(id) => (
                  <MoneyInput
                    id={id}
                    prefix="Rs."
                    className="tabular text-right text-base font-bold"
                    paisa={billDraft.totalPaisa}
                    onValue={(totalPaisa) => patchBill({ totalPaisa })}
                  />
                )}
              </Field>
            </div>
          )}
          <div className="flex gap-2 self-end">
            <Button variant="secondary" onClick={leave}>
              Cancel
            </Button>
            <Button
              loading={busy}
              disabled={invalid || !!offer}
              onClick={() => (offLines.length ? setChecking(true) : void save())}
            >
              {units > 0 && !mixedUnits ? `Receive ${qty(units)}` : 'Receive'}
            </Button>
          </div>
          {supplierId && billTyped && lines.length > 0 && (
            <div className="w-full">
              {billDraft.totalPaisa ? (
                <BillCheck
                  billPaisa={billDraft.totalPaisa}
                  linesPaisa={total}
                  shown={(p) => money(p, settings)}
                />
              ) : (
                <Banner tone="info">
                  No total, so no supplier’s bill is kept for this delivery.
                </Banner>
              )}
            </div>
          )}
        </>
      }
    >
      {paying && (
        <BillPaymentDialog
          draft={billDraft}
          onChange={patchBill}
          onClose={() => setPaying(false)}
        />
      )}
      {checking && (
        <RateCheckDialog
          off={offLines}
          onChange={changeRate}
          onClose={closeCheck}
          onReceive={(alsoSaved) => {
            setChecking(false)
            void save(alsoSaved)
          }}
        />
      )}
      <div className="space-y-4">
        {offer && (
          <DraftBanner
            savedAt={offer.savedAt}
            summary={`${linesWord(offer.value.lines.length)} · ${money(draftTotal(offer.value), settings)}`}
            onStartNew={startNew}
            onContinue={() => void continueDraft()}
          />
        )}
        <DroppedNote count={dropped} />
        <div inert={!!offer} className={cx('space-y-3', offer && 'opacity-50')}>
          <div
            className={cx(
              'grid gap-3',
              supplierId
                ? 'lg:grid-cols-[minmax(0,1fr)_10rem_auto_9rem] sm:grid-cols-2'
                : 'sm:grid-cols-[minmax(0,1fr)_10rem_9rem]',
            )}
          >
            <Field label={order ? 'Supplier' : 'Supplier (optional)'}>
              {(id) => (
                <SupplierPicker
                  id={id}
                  value={supplierId}
                  onChange={setSupplierId}
                  optional={!order}
                  disabled={!!order}
                />
              )}
            </Field>
            <Field label="Bill no.">
              {(id) => (
                <Input id={id} value={reference} onChange={(e) => setReference(e.target.value)} />
              )}
            </Field>
            {supplierId && (
              <DateInput
                label="Bill date"
                calendar={calendarOf(settings)}
                value={billDraft.billDate}
                onChange={(billDate) => patchBill({ billDate })}
              />
            )}
            <Field label="Into">
              {(id) => (
                <LocationSelect
                  id={id}
                  locations={lookups.activeLocations}
                  value={location}
                  onChange={setLocationId}
                />
              )}
            </Field>
          </div>
          {lines.length > 0 && (
            <Table stack>
              <thead>
                <tr>
                  <TH>Product</TH>
                  {showBatch && <TH className="w-36">Batch</TH>}
                  {showExpiry && <TH className="w-36">Expiry</TH>}
                  <TH align="right" className="w-20">
                    Qty
                  </TH>
                  <TH align="right" className="w-20 text-success">
                    Free
                  </TH>
                  <TH align="right" className="w-28">
                    Rate
                  </TH>
                  {showBatch && (
                    <TH align="right" className="w-24">
                      MRP
                    </TH>
                  )}
                  <TH align="right" className="w-32">
                    Amount
                  </TH>
                </tr>
              </thead>
              <tbody
                ref={linesRef}
                onKeyDown={(e) => lineKeyDown(e, () => pickerRef.current?.focus())}
              >
                {lines.map((l, i) => {
                  const active = l.key === activeKey
                  const m = moneyOf(l)
                  const free = l.freeMilli ?? 0
                  const paid = paidOf(l)
                  const special = free > 0 || m.discountPaisa > 0 || m.freeChargePaisa > 0
                  const amount = money(lineAmount({ ...l, qty: paid }), settings)
                  const cost = trueCosts[i] ?? 0
                  const note =
                    m.discountPaisa > 0
                      ? l.discount?.mode === 'percent'
                        ? `after ${percentText(l.discount.bp ?? 0)}% off`
                        : `after ${money(m.discountPaisa, settings)} off`
                      : m.freeChargePaisa > 0 && !active
                        ? `with ${money(m.freeChargePaisa, settings)} on free`
                        : null
                  const allowsDecimal = lookups.unit(l.product.unit_id)?.allows_decimal ?? false
                  // The box looks like plain text until its line is the one being typed.
                  const rest = !active && REST
                  const hideOnPhone = !active && 'max-sm:hidden'
                  const showExtras = active || l.product.track_serials
                  const r = rateOf(l)
                  const rateNote =
                    r.off && r.saved ? (
                      <span className="tabular inline-flex items-center gap-1.5 text-sm font-medium text-warning">
                        <AlertTriangle aria-hidden className="size-4 shrink-0" />
                        {r.other && `${r.perPiece}. `}Your saved cost is {money(r.saved, settings)}
                        {r.stockUnit}.
                      </span>
                    ) : r.other && r.rate > 0 ? (
                      <span className="tabular text-sm text-muted">{r.perPiece}</span>
                    ) : null
                  return (
                    <Fragment key={l.key}>
                      <tr
                        data-line={i}
                        data-main
                        onFocusCapture={() => setActiveKey(l.key)}
                        onClick={() => setActiveKey(l.key)}
                        className={cx(
                          'max-sm:grid-cols-3 max-sm:gap-x-2 max-sm:gap-y-2 max-sm:px-3',
                          active &&
                            'bg-sell-wash shadow-[inset_3px_0_0_var(--color-primary)] [&>td]:border-b-0',
                        )}
                      >
                        <TD className="max-sm:col-span-full">
                          <div className="flex items-baseline justify-between gap-3">
                            <span className="font-semibold">
                              {l.product.name}
                              {l.twoUnits && l.enteredChoice && !active && (
                                <span className="ml-1.5 text-xs font-normal text-muted">
                                  in {unitName(unitsOf(l.twoUnits), l.enteredChoice)}
                                </span>
                              )}
                            </span>
                            <span className="tabular font-semibold sm:hidden">{amount}</span>
                          </div>
                          {!active && (
                            <div className="tabular text-sm text-muted sm:hidden">
                              {qty(paid)}
                              {free > 0 && (
                                <b className="font-semibold text-success"> + {qty(free)} free</b>
                              )}{' '}
                              × {money(l.costPaisa ?? 0, settings)}
                              {note && ` (${note})`}
                            </div>
                          )}
                          {!active && rateNote && <div className="mt-0.5">{rateNote}</div>}
                          {special && paid + free > 0 && (
                            <div className="tabular text-xs text-muted">
                              {qty(paid + free)} into stock
                              {cost > 0 && `, ${money(cost, settings)} each`}
                            </div>
                          )}
                        </TD>
                        {showBatch && (
                          <TD
                            className={cx(
                              hideOnPhone,
                              'max-sm:col-span-2',
                              PHONE_LABEL,
                              "max-sm:before:content-['Batch']",
                            )}
                          >
                            {l.product.track_batches && (
                              <div className="relative">
                                <Input
                                  aria-label={`Batch no. of ${l.product.name}`}
                                  placeholder={active ? 'Batch no.' : undefined}
                                  aria-invalid={!needsQty && !l.batchNo.trim()}
                                  list={`batches-${l.key}`}
                                  value={l.batchNo}
                                  onChange={(e) => onBatchNo(i, e.target.value)}
                                  className={cx(
                                    active && 'pr-9',
                                    !needsQty && !l.batchNo.trim() && 'border-warning',
                                    rest,
                                  )}
                                />
                                {active && (
                                  <ScanButton
                                    mode="single"
                                    title="Scan the batch number"
                                    label={`Scan a batch number for ${l.product.name}`}
                                    onCode={(code) => {
                                      onBatchNo(i, code)
                                      return { ok: true, message: code }
                                    }}
                                    className="absolute bottom-0.5 right-0.5"
                                  />
                                )}
                                <datalist id={`batches-${l.key}`}>
                                  {(batchesOf[l.product.id] ?? []).map((b) => (
                                    <option key={b.id} value={b.batch_no} />
                                  ))}
                                </datalist>
                              </div>
                            )}
                          </TD>
                        )}
                        {showExpiry && (
                          <TD
                            className={cx(
                              hideOnPhone,
                              'max-sm:col-auto',
                              PHONE_LABEL,
                              "max-sm:before:content-['Expiry']",
                            )}
                          >
                            {l.product.track_batches && l.product.track_expiry && (
                              <ExpiryInput
                                name={l.product.name}
                                iso={l.expiryDate}
                                hint={active ? 'yyyy/mm' : undefined}
                                className={cx(
                                  'max-sm:px-2',
                                  !needsQty && !missingBatch && !l.expiryDate && 'border-warning',
                                  rest,
                                )}
                                onChange={(expiryDate) =>
                                  update(i, { expiryDate, expiryAuto: false })
                                }
                              />
                            )}
                          </TD>
                        )}
                        <TD
                          align="right"
                          className={cx(
                            hideOnPhone,
                            'max-sm:col-auto',
                            PHONE_LABEL,
                            "max-sm:before:content-['Qty']",
                          )}
                        >
                          {l.product.track_serials ? (
                            <span className="tabular" aria-label={`Quantity of ${l.product.name}`}>
                              {qty(qtyOf(l) ?? 0)}
                            </span>
                          ) : (
                            <QtyInput
                              aria-label={`Quantity of ${l.product.name}`}
                              milli={l.qtyMilli}
                              className={cx('tabular text-right', rest)}
                              allowsDecimal={allowsDecimal}
                              onValue={(q) => update(i, { qtyMilli: q })}
                            />
                          )}
                        </TD>
                        <TD
                          align="right"
                          className={cx(
                            hideOnPhone,
                            'max-sm:col-auto',
                            PHONE_LABEL,
                            "max-sm:before:content-['Free']",
                          )}
                        >
                          {!l.product.track_serials && (
                            <QtyInput
                              aria-label={`Free ${l.product.name}`}
                              milli={l.freeMilli}
                              placeholder="0"
                              className={cx(
                                'tabular text-right',
                                free > 0 && 'font-bold text-success',
                                rest,
                              )}
                              allowsDecimal={allowsDecimal}
                              onValue={(q) => {
                                update(i, { freeMilli: q })
                                suggestCharge(l.key, l.product, q)
                              }}
                            />
                          )}
                        </TD>
                        <TD
                          align="right"
                          className={cx(
                            hideOnPhone,
                            'max-sm:col-auto',
                            PHONE_LABEL,
                            "max-sm:before:content-['Rate']",
                          )}
                        >
                          <MoneyInput
                            aria-label={`Cost of ${l.product.name}`}
                            paisa={l.costPaisa}
                            className={cx('tabular text-right', r.off && 'border-warning', rest)}
                            onValue={(p) => update(i, { costPaisa: p })}
                          />
                        </TD>
                        {showBatch && (
                          <TD
                            align="right"
                            className={cx(
                              hideOnPhone,
                              'max-sm:col-auto',
                              PHONE_LABEL,
                              "max-sm:before:content-['MRP']",
                            )}
                          >
                            {l.product.track_batches && (
                              <MoneyInput
                                aria-label={`Batch MRP of ${l.product.name}`}
                                placeholder={active ? 'MRP' : undefined}
                                paisa={l.mrpPaisa}
                                className={cx('tabular text-right', rest && `${rest} text-muted`)}
                                onValue={(p) => update(i, { mrpPaisa: p })}
                              />
                            )}
                          </TD>
                        )}
                        <TD align="right" className="tabular font-semibold max-sm:hidden">
                          {amount}
                          {note && <div className="text-xs font-normal text-muted">{note}</div>}
                        </TD>
                      </tr>
                      {showExtras && (
                        <tr
                          data-line={i}
                          onFocusCapture={() => setActiveKey(l.key)}
                          className={cx(
                            'max-sm:block max-sm:px-3 max-sm:pt-0',
                            active && 'bg-sell-wash shadow-[inset_3px_0_0_var(--color-primary)]',
                          )}
                        >
                          <td colSpan={columns} className="border-b border-border px-3 pb-3">
                            <div className="space-y-2">
                              {l.product.track_serials && (
                                <div className="relative">
                                  <Textarea
                                    className="pr-11 font-mono"
                                    rows={3}
                                    aria-label={`Serial numbers of ${l.product.name}`}
                                    placeholder="Serial / IMEI numbers, one per line (scan each)"
                                    value={l.serialsText}
                                    onChange={(e) => update(i, { serialsText: e.target.value })}
                                  />
                                  <ScanSerialsButton
                                    text={l.serialsText}
                                    onText={(serialsText) => update(i, { serialsText })}
                                    label={`Scan serials for ${l.product.name}`}
                                    className="absolute right-1 top-1"
                                  />
                                </div>
                              )}
                              {active && (
                                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                  {l.twoUnits && l.enteredChoice && (
                                    <div className="flex items-center gap-2 text-sm font-medium text-muted">
                                      Bought in
                                      <UnitChoiceSwitch
                                        name={l.product.name}
                                        units={unitsOf(l.twoUnits)}
                                        value={l.enteredChoice}
                                        onChange={(c) => switchUnit(i, l, c)}
                                      />
                                    </div>
                                  )}
                                  {rateNote}
                                  {l.product.track_batches &&
                                    (l.mfgDate ? (
                                      <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted">
                                        Mfg date
                                        <DateInput
                                          compact
                                          label={`Mfg date of ${l.product.name}`}
                                          calendar={calendarOf(settings)}
                                          value={l.mfgDate}
                                          onChange={(iso) => onMfg(i, iso)}
                                        />
                                        <IconButton
                                          label={`Clear the mfg date of ${l.product.name}`}
                                          onClick={() => onMfg(i, '')}
                                        >
                                          <X className="size-4" />
                                        </IconButton>
                                        {l.expiryAuto &&
                                          l.expiryDate &&
                                          l.product.shelf_life_days && (
                                            <span
                                              className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary"
                                              title="From the mfg date and the shelf life"
                                            >
                                              <Sparkles className="size-3.5" />
                                              Expiry from shelf life,{' '}
                                              {shelfLifeLabel(
                                                l.product.shelf_life_days,
                                                l.product.shelf_life_unit,
                                              )}
                                            </span>
                                          )}
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className={LINK}
                                        onClick={() => onMfg(i, todayIso())}
                                      >
                                        Add mfg date
                                      </button>
                                    ))}
                                  <div className="ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-1">
                                    {!l.charge && free > 0 && (
                                      <button
                                        type="button"
                                        className={LINK}
                                        onClick={() => update(i, { charge: OPEN_BOX })}
                                      >
                                        Add charge on free
                                      </button>
                                    )}
                                    {!l.discount && paid > 0 && (
                                      <button
                                        type="button"
                                        className={LINK}
                                        onClick={() => update(i, { discount: OPEN_BOX })}
                                      >
                                        Add discount
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="text-sm font-medium text-muted hover:text-ink"
                                      onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                                    >
                                      Remove line
                                    </button>
                                  </div>
                                </div>
                              )}
                              {active && l.charge && free > 0 && (
                                <ExtraBox
                                  label={`Charge on the ${qty(free)} free`}
                                  name={`Charge on free ${l.product.name}`}
                                  box={l.charge}
                                  result={`adds ${money(m.freeChargePaisa, settings)}`}
                                  onChange={(charge) => update(i, { charge })}
                                />
                              )}
                              {active && l.discount && (
                                <ExtraBox
                                  label="Discount"
                                  name={`Line discount on ${l.product.name}`}
                                  box={l.discount}
                                  result={`takes off ${money(m.discountPaisa, settings)}`}
                                  onChange={(discount) => update(i, { discount })}
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </Table>
          )}
          {billDiscount && (
            <div className="flex justify-end">
              <ExtraBox
                label="Discount on the whole bill"
                name="Discount on the whole bill"
                box={billDiscount}
                result={`takes off ${money(billOff, settings)}`}
                onChange={setBillDiscount}
              />
            </div>
          )}
          {finePointer && lines.length > 1 && (
            <p className="flex items-center gap-3 text-xs text-muted max-sm:hidden">
              <span className="inline-flex items-center gap-1">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd> line
              </span>
              <span className="inline-flex items-center gap-1">
                <Kbd>Enter</Kbd> next box
              </span>
            </p>
          )}
          <div
            onKeyDownCapture={(e) => {
              const last = [
                ...e.currentTarget.closest('[role="dialog"]')!.querySelectorAll('tr[data-main]'),
              ].pop()
              if (e.key !== 'ArrowUp' || pickerRef.current?.value || !last) return
              e.preventDefault()
              e.stopPropagation()
              const box = last.querySelector<HTMLInputElement>('input[aria-label^="Quantity of"]')
              box?.focus()
              box?.select()
            }}
          >
            <ProductPicker
              inputRef={pickerRef}
              onPick={(p, pack) => add(p, pack)}
              placeholder={
                order
                  ? 'Something came that was not on the order? Scan or search it…'
                  : 'Scan or search products to receive…'
              }
              autoFocus={finePointer}
              addNew="product"
              groups={clothing}
              onPickGroup={setGridOf}
            />
          </div>
        </div>
      </div>
      {gridOf && (
        <ReceiveGrid
          group={gridOf}
          onClose={() => setGridOf(null)}
          onAdd={(rows) => {
            setGridOf(null)
            for (const r of rows) add(r.product, null, r.qtyMilli)
          }}
        />
      )}
    </Modal>
  )
}
